import React, { useState, useEffect, useRef } from 'react';
import { Box, IconButton, MobileStepper, Dialog, Fade } from '@mui/material';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';

interface ImageCarouselProps {
  images: string[];
  positions?: string[];
  artistName: string;
  height?: number;
  autoRotate?: boolean;
  rotateInterval?: number;
}

const ImageCarousel: React.FC<ImageCarouselProps> = ({
  images,
  positions,
  artistName,
  height = 500,
  autoRotate = true,
  rotateInterval = 10000
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set([0]));
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  const maxSteps = images.length;
  const minSwipeDistance = 50;
  
  // Ken Burns effect variants (randomize zoom direction for each image)
  const kenBurnsVariants = [
    'kenburns-top-left',
    'kenburns-top-right', 
    'kenburns-bottom-left',
    'kenburns-bottom-right',
    'kenburns-center'
  ];

  // Preload adjacent images
  useEffect(() => {
    const toLoad = new Set(loadedImages);
    toLoad.add(activeStep);
    if (activeStep > 0) toLoad.add(activeStep - 1);
    if (activeStep < maxSteps - 1) toLoad.add(activeStep + 1);
    
    toLoad.forEach(index => {
      if (!loadedImages.has(index)) {
        const img = new Image();
        img.src = images[index];
      }
    });
    
    setLoadedImages(toLoad);
  }, [activeStep, images, maxSteps, loadedImages]);

  // Auto-rotate
  useEffect(() => {
    if (!autoRotate || maxSteps <= 1 || isPaused || lightboxOpen) return;
    
    const timer = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % maxSteps);
    }, rotateInterval);
    
    return () => clearInterval(timer);
  }, [autoRotate, maxSteps, isPaused, rotateInterval, lightboxOpen]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (lightboxOpen) {
        if (e.key === 'Escape') setLightboxOpen(false);
        if (e.key === 'ArrowLeft') handleBack();
        if (e.key === 'ArrowRight') handleNext();
      } else if (containerRef.current?.contains(document.activeElement)) {
        if (e.key === 'ArrowLeft') handleBack();
        if (e.key === 'ArrowRight') handleNext();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeStep, maxSteps, lightboxOpen]);

  const handleNext = () => {
    setActiveStep((prev) => (prev + 1) % maxSteps);
  };

  const handleBack = () => {
    setActiveStep((prev) => (prev - 1 + maxSteps) % maxSteps);
  };

  const handleDotClick = (index: number) => {
    setActiveStep(index);
  };

  // Touch handlers for swipe
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) handleNext();
    if (isRightSwipe) handleBack();
  };

  if (images.length === 0) return null;

  return (
    <>
      <Box 
        ref={containerRef}
        sx={{ position: 'relative', width: '100%', cursor: 'pointer' }}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        tabIndex={0}
      >
        {/* Images with fade transition */}
        <Box sx={{ 
          position: 'relative', 
          height, 
          overflow: 'hidden', 
          borderRadius: 1,
          '@keyframes kenburns-top-left': {
            '0%': { transform: 'scale(1) translate(0, 0)' },
            '100%': { transform: 'scale(1.15) translate(-2%, -2%)' }
          },
          '@keyframes kenburns-top-right': {
            '0%': { transform: 'scale(1) translate(0, 0)' },
            '100%': { transform: 'scale(1.15) translate(2%, -2%)' }
          },
          '@keyframes kenburns-bottom-left': {
            '0%': { transform: 'scale(1) translate(0, 0)' },
            '100%': { transform: 'scale(1.15) translate(-2%, 2%)' }
          },
          '@keyframes kenburns-bottom-right': {
            '0%': { transform: 'scale(1) translate(0, 0)' },
            '100%': { transform: 'scale(1.15) translate(2%, 2%)' }
          },
          '@keyframes kenburns-center': {
            '0%': { transform: 'scale(1)' },
            '100%': { transform: 'scale(1.15)' }
          }
        }}>
          {images.map((img, index) => (
            <Fade key={index} in={activeStep === index} timeout={600}>
              <Box
                component="img"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  height: '100%',
                  width: '100%',
                  objectFit: 'cover',
                  objectPosition: positions?.[index] || '50% 50%',
                  display: activeStep === index ? 'block' : 'none',
                  animation: activeStep === index && autoRotate && !isPaused
                    ? `${kenBurnsVariants[index % kenBurnsVariants.length]} ${rotateInterval}ms ease-in-out infinite alternate`
                    : 'none',
                  transformOrigin: positions?.[index] || 'center center',
                }}
                src={img}
                alt={`${artistName} - Image ${index + 1}`}
                onClick={() => setLightboxOpen(true)}
                loading={loadedImages.has(index) ? 'eager' : 'lazy'}
              />
            </Fade>
          ))}
        </Box>

        {/* Fullscreen button */}
        <IconButton
          onClick={() => setLightboxOpen(true)}
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            bgcolor: 'rgba(0,0,0,0.5)',
            color: 'white',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
            zIndex: 2
          }}
        >
          <FullscreenIcon />
        </IconButton>

        {/* Navigation Arrows - only show if multiple images */}
        {maxSteps > 1 && (
          <>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleBack();
              }}
              sx={{
                display: { xs: 'none', sm: 'flex' },
                position: 'absolute',
                left: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(0,0,0,0.5)',
                color: 'white',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                zIndex: 2
              }}
            >
              <KeyboardArrowLeft />
            </IconButton>

            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleNext();
              }}
              sx={{
                display: { xs: 'none', sm: 'flex' },
                position: 'absolute',
                right: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(0,0,0,0.5)',
                color: 'white',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                zIndex: 2
              }}
            >
              <KeyboardArrowRight />
            </IconButton>

            {/* Clickable Stepper Dots */}
            <MobileStepper
              steps={maxSteps}
              position="static"
              activeStep={activeStep}
              sx={{
                position: 'absolute',
                bottom: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                bgcolor: 'rgba(0,0,0,0.5)',
                borderRadius: 2,
                width: 'auto',
                padding: '8px 16px',
                zIndex: 2,
                '& .MuiMobileStepper-dots': {
                  gap: '8px'
                },
                '& .MuiMobileStepper-dot': {
                  bgcolor: 'rgba(255,255,255,0.4)',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.7)',
                    transform: 'scale(1.2)'
                  }
                },
                '& .MuiMobileStepper-dotActive': {
                  bgcolor: 'white'
                }
              }}
              nextButton={<span />}
              backButton={<span />}
            />

            {/* Make dots clickable by overlaying invisible buttons */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: '8px',
                padding: '8px 16px',
                zIndex: 3
              }}
            >
              {images.map((_, index) => (
                <Box
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDotClick(index);
                  }}
                  sx={{
                    width: 44,
                    height: 44,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '-18px -18px',
                  }}
                />
              ))}
            </Box>

            {/* Image counter */}
            <Box
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                bgcolor: 'rgba(0,0,0,0.7)',
                color: 'white',
                px: 2,
                py: 0.5,
                borderRadius: 1,
                fontSize: '0.875rem',
                fontWeight: 600,
                zIndex: 2
              }}
            >
              {activeStep + 1} / {maxSteps}
            </Box>
          </>
        )}
      </Box>

      {/* Lightbox Dialog */}
      <Dialog
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            bgcolor: 'rgba(0,0,0,0.95)',
            boxShadow: 'none',
            m: 0,
            maxHeight: '100vh',
            maxWidth: '100vw'
          }
        }}
      >
        <Box sx={{ position: 'relative', width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {/* Close button */}
          <IconButton
            onClick={() => setLightboxOpen(false)}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              color: 'white',
              bgcolor: 'rgba(0,0,0,0.5)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
              zIndex: 1301
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Lightbox image */}
          <Box
            component="img"
            src={images[activeStep]}
            alt={`${artistName} - Image ${activeStep + 1}`}
            sx={{
              maxWidth: '95%',
              maxHeight: '95%',
              objectFit: 'contain'
            }}
          />

          {/* Lightbox navigation */}
          {maxSteps > 1 && (
            <>
              <IconButton
                onClick={handleBack}
                sx={{
                  position: 'absolute',
                  left: 32,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  bgcolor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                }}
              >
                <KeyboardArrowLeft fontSize="large" />
              </IconButton>

              <IconButton
                onClick={handleNext}
                sx={{
                  position: 'absolute',
                  right: 32,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  bgcolor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                }}
              >
                <KeyboardArrowRight fontSize="large" />
              </IconButton>

              {/* Lightbox counter */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 32,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  bgcolor: 'rgba(0,0,0,0.7)',
                  color: 'white',
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  fontSize: '1rem',
                  fontWeight: 600
                }}
              >
                {activeStep + 1} / {maxSteps}
              </Box>
            </>
          )}
        </Box>
      </Dialog>
    </>
  );
};

export default ImageCarousel;